import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { setTheme } from "@/lib/theme";
import { toast } from "@/components/ui/sonner";
import { Plus, Library, RotateCcw, CheckCircle2, Circle, Sparkles, ArrowRight, Layers, Loader2 } from "lucide-react";
import { WorkspaceSidebar } from "@/components/WorkspaceSidebar";
import { BrandDNAPanel } from "@/components/BrandDNAPanel";
import { MarketResearchPanel } from "@/components/MarketResearchPanel";
import { VoiceCardPanel } from "@/components/VoiceCardPanel";
import { LogoPanel } from "@/components/LogoPanel";
import { BrandKitHealth } from "@/components/BrandKitHealth";
import { ContentPillarsPanel } from "@/components/ContentPillarsPanel";
import { StudioPanel } from "@/components/StudioPanel";
import { ReviewDeck } from "@/components/ReviewDeck";
import { PreviewPublishPanel } from "@/components/PreviewPublishPanel";
import { SavedBrandsSheet } from "@/components/SavedBrandsSheet";
import { SavedContentSheet } from "@/components/SavedContentSheet";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { AccountTools } from "@/components/AccountTools";

let tempCounter = 0;
const ONBOARD_KEY = "hdg_onboarded";

const SECTIONS = {
  brand: { title: "Brand DNA", sub: "Analyze a website to extract its palette, voice & assets." },
  create: { title: "Create", sub: "Generate on-brand ideas, then render finished visuals." },
  review: { title: "Review", sub: "Swipe through rendered posts — approve to queue, skip to dismiss." },
  calendar: { title: "Calendar", sub: "Everything you've scheduled, in one place." },
  publish: { title: "Publish", sub: "Connect Publer and push approved content live." },
  settings: { title: "Settings", sub: "Account, plan usage & integrations." },
};

export default function Studio() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState({
    appName: "HDG Creative Studio",
    clientName: "HeyDesignGuy",
    poweredBy: "Powered by HeyDesignGuy",
    poweredByUrl: "https://heydesignguy.com",
    assetTypes: [],
    connections: [],
  });
  const [brandDna, setBrandDna] = useState(null);
  const [brandId, setBrandId] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [images, setImages] = useState([]);
  const [savedBrands, setSavedBrands] = useState([]);
  const [brandsOpen, setBrandsOpen] = useState(false);
  const [contentOpen, setContentOpen] = useState(false);
  const [section, setSection] = useState("brand");
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [usage, setUsage] = useState(null);
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [seedDirection, setSeedDirection] = useState(null);
  const [scheduleVersion, setScheduleVersion] = useState(0);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWsId, setActiveWsId] = useState(() => localStorage.getItem("hdg_ws") || null);

  const loadWorkspaces = useCallback(() => {
    api.get("/workspaces").then(({ data }) => {
      const list = data.workspaces || [];
      setWorkspaces(list);
      setActiveWsId((cur) => {
        const valid = cur && list.some((w) => w.id === cur);
        const next = valid ? cur : (list[0]?.id || null);
        if (next) localStorage.setItem("hdg_ws", next);
        return next;
      });
    }).catch(() => {});
  }, []);

  const useAngle = useCallback((text) => {
    setSeedDirection({ text, key: Date.now() });
    setSection("create");
    toast.success("Angle applied", { description: "Review the creative direction, then generate." });
  }, []);

  const loadBrands = useCallback(() => {
    api.get("/brands").then(({ data }) => setSavedBrands(data.brands || [])).catch(() => {});
  }, []);

  const refreshUsage = useCallback(() => {
    api.get("/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, []);

  const handleLimit = useCallback((info) => {
    setUpgradeInfo(info);
    refreshUsage();
  }, [refreshUsage]);

  useEffect(() => {
    setTheme("workspace");
    api
      .get("/config")
      .then(({ data }) => {
        setConfig((c) => ({ ...c, ...data }));
        return api.get("/publer/status");
      })
      .then((res) => {
        if (!res) return;
        const ok = !!res.data.connected;
        setConfig((c) => ({
          ...c,
          connections: (c.connections || []).map((conn) =>
            conn.key === "publer" ? { ...conn, connected: ok } : conn
          ),
        }));
      })
      .catch(() => {});
    loadBrands();
    refreshUsage();
    if (!localStorage.getItem(ONBOARD_KEY)) setOnboardOpen(true);
  }, [loadBrands, refreshUsage]);

  const closeOnboarding = useCallback(() => {
    localStorage.setItem(ONBOARD_KEY, "1");
    setOnboardOpen(false);
  }, []);

  const handleAnalyzed = useCallback((dna) => {
    setBrandDna(dna);
    setBrandId(dna?.id || null);
    setIdeas([]);
    setImages([]);
    loadBrands();
    refreshUsage();
  }, [loadBrands, refreshUsage]);

  const loadBrand = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/brands/${id}`);
      const b = data.brand;
      setBrandDna(b);
      setBrandId(b.id);
      setIdeas((b.ideas || []).map((it, i) => ({ ...it, id: it.id || `idea-${i}-${Date.now()}`, slides: it.slides || 3 })));
      setImages((data.assets || []).map((g) => ({
        tempId: `t${++tempCounter}-${Date.now()}`,
        groupId: g.groupId,
        status: "done",
        assetType: g.assetType,
        aspect: g.aspect,
        caption: g.caption,
        title: g.title,
        slides: (g.items || []).length,
        items: g.items || [],
      })));
      setBrandsOpen(false);
      setContentOpen(false);
      setSection("brand");
      toast.success("Brand loaded", { description: b.siteName });
    } catch (e) {
      toast.error(errMsg(e, "Could not load brand"));
    }
  }, []);

  const newSession = useCallback(() => {
    setBrandDna(null);
    setBrandId(null);
    setIdeas([]);
    setImages([]);
    setBrandsOpen(false);
    setSection("brand");
  }, []);

  const switchWorkspace = useCallback((id) => {
    if (!id || id === activeWsId) return;
    localStorage.setItem("hdg_ws", id);
    setActiveWsId(id);
    newSession();
    loadBrands();
    refreshUsage();
    loadWorkspaces();
    const ws = workspaces.find((w) => w.id === id);
    toast.success("Workspace switched", { description: ws?.name });
  }, [activeWsId, newSession, loadBrands, refreshUsage, loadWorkspaces, workspaces]);

  const createWorkspace = useCallback(async (name) => {
    try {
      const { data } = await api.post("/workspaces", { name });
      const id = data.workspace.id;
      localStorage.setItem("hdg_ws", id);
      setActiveWsId(id);
      newSession();
      loadBrands();
      refreshUsage();
      loadWorkspaces();
      toast.success("Workspace created", { description: data.workspace.name });
    } catch (e) {
      toast.error(errMsg(e, "Could not create workspace"));
    }
  }, [newSession, loadBrands, refreshUsage, loadWorkspaces]);

  // Load workspaces on mount + handle return from Stripe checkout.
  useEffect(() => {
    loadWorkspaces();
    const params = new URLSearchParams(window.location.search);
    if (params.get("billing") === "success" && params.get("session_id")) {
      const sid = params.get("session_id");
      window.history.replaceState({}, "", "/app");
      setSection("settings");
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.paymentStatus === "paid") {
            toast.success(`Payment confirmed — ${data.tier} plan active`, { description: "Your AI allowance has been topped up." });
            refreshUsage();
            loadWorkspaces();
            return;
          }
        } catch { /* ignore */ }
        if (tries < 6) setTimeout(poll, 2000);
        else toast("Still confirming your payment — refresh in a moment.");
      };
      poll();
    } else if (params.get("billing") === "cancel") {
      window.history.replaceState({}, "", "/app");
      toast("Checkout canceled");
    }
  }, [loadWorkspaces, refreshUsage]);

  const handleLogout = useCallback(async () => {
    await logout();
    navigate("/");
  }, [logout, navigate]);

  // Auto-load the brand a just-signed-up user analyzed during onboarding.
  useEffect(() => {
    const openId = sessionStorage.getItem("hdg_open_brand");
    if (openId) {
      sessionStorage.removeItem("hdg_open_brand");
      loadBrand(openId);
    }
  }, [loadBrand]);

  const deleteBrand = useCallback(async (id) => {
    try {
      await api.delete(`/brands/${id}`);
      if (id === brandId) newSession();
      loadBrands();
      refreshUsage();
      toast.success("Brand deleted");
    } catch (e) {
      toast.error(errMsg(e, "Could not delete brand"));
    }
  }, [brandId, newSession, loadBrands, refreshUsage]);

  const addImage = useCallback((img) => {
    const tempId = `t${++tempCounter}-${Date.now()}`;
    setImages((prev) => [{ tempId, items: [], ...img }, ...prev]);
    return tempId;
  }, []);

  const updateImage = useCallback((tempId, patch) => {
    setImages((prev) => prev.map((im) => (im.tempId === tempId ? { ...im, ...patch } : im)));
  }, []);

  const removeImage = useCallback((tempId) => {
    setImages((prev) => prev.filter((im) => im.tempId !== tempId));
  }, []);

  const [batchBusy, setBatchBusy] = useState(false);
  const generateBatch = useCallback(async () => {
    if (!brandId || batchBusy) return;
    setBatchBusy(true);
    try {
      const { data } = await api.post("/posts/batch", { brandId, count: 5 });
      refreshUsage();
      const posts = data.posts || [];
      if (!posts.length) {
        toast.error("No posts generated — try again");
        setBatchBusy(false);
        return;
      }
      setSection("review");
      toast.success(`Drafting ${posts.length} posts for the week`, { description: "They'll appear in the deck as they render." });
      posts.forEach((p) => {
        const tempId = addImage({
          groupId: p.groupId,
          caption: p.caption,
          captions: p.captions || { [p.platform]: p.caption },
          platforms: p.platforms || [p.platform],
          brandId,
          colors: brandDna?.colors || [],
          siteName: brandDna?.siteName || "",
          assetType: "Social Post",
          title: p.title,
          aspect: p.aspect,
          hashtags: p.hashtags,
          pillar: p.pillar,
          suggestedDay: p.suggestedDay,
          suggestedTime: p.suggestedTime,
          items: [],
          status: "rendering",
        });
        let attempts = 0;
        const poll = async () => {
          attempts += 1;
          try {
            const { data: g } = await api.get(`/images/group/${p.groupId}`);
            updateImage(tempId, { items: g.items, aspect: g.aspect, status: g.status === "done" ? "done" : "rendering" });
            if (g.status === "rendering" && attempts < 45) setTimeout(poll, 2000);
            else if (g.status === "error") removeImage(tempId);
          } catch {
            if (attempts < 45) setTimeout(poll, 2500);
            else updateImage(tempId, { status: "done" });
          }
        };
        poll();
      });
    } catch (e) {
      const info = e?.response?.data?.detail;
      if (info?.code === "limit_reached") handleLimit(info);
      else toast.error(errMsg(e, "Could not generate the batch"));
    } finally {
      setBatchBusy(false);
    }
  }, [brandId, batchBusy, brandDna, addImage, updateImage, removeImage, refreshUsage, handleLimit]);

  const generateKit = useCallback(async (kind) => {
    if (!brandId) return;
    const ep = kind === "voice" ? "/brand/voice-card" : "/brand/pillars";
    const { data } = await api.post(ep, { brandId, force: true });
    setBrandDna((d) => (d ? { ...d, ...(kind === "voice" ? { voiceCard: data.voiceCard } : { pillars: data.pillars }) } : d));
    refreshUsage();
  }, [brandId, refreshUsage]);

  const meta = SECTIONS[section];
  const publerConn = (config.connections || []).find((c) => c.key === "publer");

  return (
    <div className="lightboard" style={{ height: "100vh", display: "flex", overflow: "hidden" }} data-testid="studio-root">
      <WorkspaceSidebar
        section={section}
        setSection={setSection}
        appName={config.appName}
        usage={usage}
        workspaces={workspaces}
        activeWsId={activeWsId}
        onSwitchWorkspace={switchWorkspace}
        onCreateWorkspace={createWorkspace}
        poweredBy={config.poweredBy}
        poweredByUrl={config.poweredByUrl}
        onLogout={handleLogout}
        brands={savedBrands}
        currentBrandId={brandId}
        currentName={brandDna?.siteName || brandDna?.brandName}
        onLoadBrand={loadBrand}
        onNewBrand={newSession}
      />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
        {/* header */}
        <header
          className="flex items-center justify-between gap-4 px-5 lg:px-8"
          style={{ height: 64, borderBottom: "1px solid var(--cardborder)", background: "var(--panel)", flexShrink: 0 }}
        >
          <div style={{ minWidth: 0 }}>
            <h1 className="font-display" style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", color: "var(--tprimary)", lineHeight: 1.1 }} data-testid="section-title">{meta.title}</h1>
            <p className="font-body hidden sm:block" style={{ fontSize: 12.5, color: "var(--tsecondary)", margin: "2px 0 0" }}>{meta.sub}</p>
          </div>
          <div className="flex items-center gap-2.5" style={{ flexShrink: 0 }}>
            {brandDna && (
              <span className="hidden md:inline-flex font-mono" style={{ alignItems: "center", gap: 6, fontSize: 11, padding: "5px 10px", borderRadius: 999, border: "1px solid var(--cardborder)", color: "var(--tsecondary)", background: "var(--surface)" }} data-testid="active-brand-chip">
                {brandDna.siteName || brandDna.brandName}
              </span>
            )}
            <button onClick={() => setBrandsOpen(true)} className="btn-secondary font-mono" data-testid="header-brands-button" style={{ fontSize: 12, padding: "8px 12px" }}>
              Brands ({savedBrands.length})
            </button>
            <button onClick={newSession} className="btn-primary font-mono" data-testid="header-new-brand-button" style={{ fontSize: 12, padding: "8px 14px" }}>
              <Plus size={14} /> <span className="hidden sm:inline">New Brand</span>
            </button>
          </div>
        </header>

        {/* content */}
        <div className="thin-scroll" style={{ flex: 1, overflowY: "auto", padding: "28px 20px" }} data-testid="workspace-content">
          <div style={{ maxWidth: section === "create" ? 1240 : 900, margin: "0 auto" }}>

            {section === "brand" && (
              <div className="flex flex-col gap-6" data-testid="section-brand">
                <div className="panel-card" style={{ padding: 0, overflow: "hidden" }}>
                  <BrandDNAPanel
                    brandDna={brandDna}
                    setBrandDna={setBrandDna}
                    onAnalyzed={handleAnalyzed}
                    brandId={brandId}
                    onLimit={handleLimit}
                    refreshUsage={refreshUsage}
                  />
                </div>
                {brandId && brandDna && (
                  <BrandKitHealth brand={brandDna} onGenerate={generateKit} />
                )}
                {brandId && brandDna && (
                  <LogoPanel key={`logo-${!!brandDna.logo}`} brandId={brandId} logo={brandDna.logo} />
                )}
                {brandId && brandDna && (
                  <VoiceCardPanel key={`voice-${!!brandDna.voiceCard}`} brandId={brandId} initialVoiceCard={brandDna.voiceCard} />
                )}
                {brandId && brandDna && (
                  <ContentPillarsPanel key={`pillars-${(brandDna.pillars || []).length > 0}`} brandId={brandId} initialPillars={brandDna.pillars} />
                )}
                {brandId && brandDna && (
                  <MarketResearchPanel brandId={brandId} initialResearch={brandDna.research} onUseAngle={useAngle} />
                )}
              </div>
            )}

            {section === "create" && (
              brandDna ? (
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-6" data-testid="section-create">
                  <div className="panel-card" style={{ padding: 0, overflow: "hidden" }}>
                    <StudioPanel
                      brandDna={brandDna}
                      brandId={brandId}
                      ideas={ideas}
                      setIdeas={setIdeas}
                      assetTypes={config.assetTypes}
                      seedDirection={seedDirection}
                      addImage={addImage}
                      updateImage={updateImage}
                      removeImage={removeImage}
                      clearImages={() => setImages([])}
                      hasPreviews={images.length > 0}
                      onLimit={handleLimit}
                      refreshUsage={refreshUsage}
                    />
                  </div>
                  <div className="panel-card" style={{ padding: 20 }}>
                    <PreviewPublishPanel images={images} view="preview" onLimit={handleLimit} refreshUsage={refreshUsage} />
                  </div>
                </div>
              ) : (
                <EmptyHint
                  icon={Sparkles}
                  title="Pick a brand first"
                  hint="Analyze a website in Brand DNA to unlock idea generation and asset rendering."
                  actionLabel="Go to Brand DNA"
                  onAction={() => setSection("brand")}
                />
              )
            )}

            {section === "review" && (
              brandDna ? (
                <div className="flex flex-col gap-5" data-testid="section-review">
                  <div className="panel-card flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" style={{ padding: 16 }}>
                    <div>
                      <div className="font-display" style={{ fontSize: 14.5, fontWeight: 600, color: "var(--tprimary)" }}>Batch generate a week of posts</div>
                      <div className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", marginTop: 2 }}>One click drafts 5 on-brand posts spread across your platforms — review them right here.</div>
                    </div>
                    <button
                      data-testid="generate-week-button"
                      onClick={generateBatch}
                      disabled={batchBusy}
                      className="btn-primary font-mono"
                      style={{ fontSize: 12.5, padding: "10px 16px", whiteSpace: "nowrap", opacity: batchBusy ? 0.6 : 1 }}
                    >
                      {batchBusy ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} Generate a week of posts
                    </button>
                  </div>
                  <ReviewDeck
                    images={images}
                    onLimit={handleLimit}
                    refreshUsage={refreshUsage}
                    onApproved={() => setScheduleVersion((v) => v + 1)}
                  />
                </div>
              ) : (
                <EmptyHint
                  icon={Layers}
                  title="Nothing to review yet"
                  hint="Generate ideas and render a few concepts in Create, then come back to swipe through them."
                  actionLabel="Go to Create"
                  onAction={() => setSection("create")}
                />
              )
            )}

            {section === "calendar" && (
              <div className="panel-card" style={{ padding: 20 }} data-testid="section-calendar">
                <PreviewPublishPanel images={images} view="calendar" reloadKey={scheduleVersion} onLimit={handleLimit} refreshUsage={refreshUsage} />
              </div>
            )}

            {section === "publish" && (
              <div className="flex flex-col gap-5" data-testid="section-publish">
                <div className="panel-card" style={{ padding: 22 }}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      {publerConn?.connected ? <CheckCircle2 size={20} style={{ color: "var(--ok)" }} /> : <Circle size={20} style={{ color: "var(--tmuted)" }} />}
                      <div>
                        <div className="font-display" style={{ fontWeight: 600, fontSize: 15, color: "var(--tprimary)" }}>Publer</div>
                        <div className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)" }}>
                          {publerConn?.connected ? "Connected — scheduled posts sync to your Publer workspace." : "Offline — scheduled posts save locally to your Calendar."}
                        </div>
                      </div>
                    </div>
                    <span className="font-mono" style={{ fontSize: 10, padding: "5px 10px", borderRadius: 999, textTransform: "uppercase", letterSpacing: "0.06em", border: "1px solid var(--cardborder)", color: publerConn?.connected ? "var(--ok)" : "var(--tmuted)" }}>
                      {publerConn?.connected ? "Connected" : "Offline"}
                    </span>
                  </div>
                  <p className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", margin: "14px 0 0", lineHeight: 1.5 }}>
                    To publish: render a visual in <button onClick={() => setSection("create")} className="app-link" style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--tprimary)", fontWeight: 600 }}>Create</button>, open it, then choose a date & time to schedule.
                  </p>
                </div>
                <div className="panel-card" style={{ padding: 20 }}>
                  <div className="eyebrow" style={{ marginBottom: 12 }}>Scheduled queue</div>
                  <PreviewPublishPanel images={images} view="calendar" reloadKey={scheduleVersion} onLimit={handleLimit} refreshUsage={refreshUsage} />
                </div>
              </div>
            )}

            {section === "settings" && (
              <SettingsSection
                usage={usage}
                workspaces={workspaces}
                activeWsId={activeWsId}
                connections={config.connections}
                onReplayOnboarding={() => setOnboardOpen(true)}
                onOpenContent={() => setContentOpen(true)}
                onLogout={handleLogout}
                poweredBy={config.poweredBy}
                poweredByUrl={config.poweredByUrl}
                onDataDeleted={() => { setBrandDna(null); setBrandId(null); setIdeas([]); setImages([]); loadBrands(); refreshUsage(); }}
              />
            )}
          </div>
        </div>
      </div>

      <SavedBrandsSheet
        open={brandsOpen}
        onOpenChange={setBrandsOpen}
        brands={savedBrands}
        currentId={brandId}
        onLoad={loadBrand}
        onNew={newSession}
        onDelete={deleteBrand}
      />

      <SavedContentSheet
        open={contentOpen}
        onOpenChange={setContentOpen}
        onOpenBrand={loadBrand}
      />

      <OnboardingWizard open={onboardOpen} onClose={closeOnboarding} />
      <UpgradeDialog info={upgradeInfo} onClose={() => setUpgradeInfo(null)} />
    </div>
  );
}

function EmptyHint({ icon: Icon, title, hint, actionLabel, onAction }) {
  return (
    <div className="panel-card flex flex-col items-center text-center" style={{ padding: "52px 28px", maxWidth: 460, margin: "40px auto" }} data-testid="create-empty">
      <div style={{ width: 48, height: 48, borderRadius: 12, display: "grid", placeItems: "center", background: "var(--surface)", border: "1px solid var(--cardborder)", marginBottom: 16 }}>
        <Icon size={22} style={{ color: "var(--tsecondary)" }} />
      </div>
      <h3 className="font-display" style={{ fontWeight: 700, fontSize: 17, color: "var(--tprimary)", margin: "0 0 6px" }}>{title}</h3>
      <p className="font-body" style={{ fontSize: 13, color: "var(--tsecondary)", margin: "0 0 20px", lineHeight: 1.55 }}>{hint}</p>
      <button onClick={onAction} className="btn-primary font-mono" data-testid="create-empty-action" style={{ fontSize: 12, padding: "10px 16px" }}>
        {actionLabel} <ArrowRight size={14} />
      </button>
    </div>
  );
}

function UsageRow({ label, value }) {
  return (
    <div className="flex items-center justify-between" style={{ padding: "9px 0", borderBottom: "1px solid var(--cardborder)" }}>
      <span className="font-body" style={{ fontSize: 13, color: "var(--tsecondary)", textTransform: "capitalize" }}>{label}</span>
      <span className="font-mono" style={{ fontSize: 13, color: "var(--tprimary)", fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function SettingsSection({ usage, workspaces, activeWsId, connections, onReplayOnboarding, onOpenContent, onLogout, poweredBy, poweredByUrl, onDataDeleted }) {
  const tier = usage?.tier || "trial";
  const allowance = usage?.allowance ?? 0;
  const postsUsed = usage?.postsUsed ?? 0;
  const remaining = usage?.postsRemaining ?? Math.max(0, allowance - postsUsed);
  const pct = allowance ? Math.min(100, Math.round((postsUsed / allowance) * 100)) : 0;
  const ws = (workspaces || []).find((w) => w.id === activeWsId);
  const TIERS = [
    { tier: "Starter", price: "$249", allowance: 40, tagline: "Up to 40 generated posts / month" },
    { tier: "Pro", price: "$499", allowance: 120, tagline: "Up to 120 generated posts / month" },
    { tier: "Enterprise", price: "$1,499", allowance: 500, tagline: "Up to 500 generated posts / month" },
  ];

  const startCheckout = async (t) => {
    try {
      const { data } = await api.post("/payments/checkout", { tier: t, originUrl: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      toast.error(errMsg(e, "Could not start checkout"));
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="section-settings">
      <div className="panel-card" style={{ padding: 22 }} data-testid="settings-allowance">
        <div className="eyebrow" style={{ marginBottom: 14 }}>AI allowance · {ws?.name || "Workspace"}</div>
        <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
          <span className="font-display" style={{ fontSize: 16, fontWeight: 700, color: "var(--tprimary)", textTransform: "capitalize" }}>{tier} plan</span>
          <span className="font-mono" style={{ fontSize: 12, color: "var(--tsecondary)" }} data-testid="allowance-readout">{postsUsed} / {allowance} posts · {remaining} left</span>
        </div>
        {usage?.onTrial && (
          <div data-testid="trial-banner" className="font-mono" style={{ fontSize: 11.5, color: "var(--orange)", marginBottom: 10 }}>
            Free trial · {usage.trialDaysLeft} day{usage.trialDaysLeft === 1 ? "" : "s"} left — upgrade anytime to keep generating.
          </div>
        )}
        <div style={{ height: 8, borderRadius: 999, background: "var(--surface)", overflow: "hidden", border: "1px solid var(--cardborder)" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: pct >= 100 ? "var(--err)" : "var(--orange)", transition: "width 0.3s ease" }} />
        </div>
        <div className="grid grid-cols-3 gap-3" style={{ marginTop: 16 }}>
          <UsageRow label="brands" value={usage?.used?.brands ?? 0} />
          <UsageRow label="ideas" value={usage?.used?.ideas ?? 0} />
          <UsageRow label="scheduled" value={usage?.used?.scheduled ?? 0} />
        </div>
      </div>

      <div className="panel-card" style={{ padding: 22 }} data-testid="settings-billing">
        <div className="eyebrow" style={{ marginBottom: 6 }}>Billing &amp; plan</div>
        <p className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", margin: "0 0 16px" }}>
          Upgrade this workspace to unlock more monthly AI renders. Secure checkout via Stripe.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {TIERS.map((t) => {
            const current = tier === t.tier;
            return (
              <div key={t.tier} className="panel-card" data-testid={`billing-tier-${t.tier.toLowerCase()}`} style={{ padding: 18, border: current ? "1px solid var(--orange)" : "1px solid var(--cardborder)" }}>
                <div className="flex items-center justify-between">
                  <span className="font-display" style={{ fontSize: 18, fontWeight: 700, color: "var(--tprimary)" }}>{t.tier}</span>
                  {current && <span className="font-mono" style={{ fontSize: 10, color: "var(--orange)", textTransform: "uppercase" }}>Current</span>}
                </div>
                <div className="font-display" style={{ fontSize: 24, fontWeight: 700, color: "var(--tprimary)", margin: "6px 0 2px" }}>{t.price}<span className="font-body" style={{ fontSize: 12, color: "var(--tmuted)" }}>/mo</span></div>
                <div className="font-body" style={{ fontSize: 12.5, color: "var(--tsecondary)", marginBottom: 14 }}>{t.tagline}</div>
                <button
                  data-testid={`upgrade-${t.tier.toLowerCase()}-button`}
                  onClick={() => startCheckout(t.tier)}
                  disabled={current}
                  className="btn-primary font-mono"
                  style={{ width: "100%", padding: "10px 0", fontSize: 12, opacity: current ? 0.5 : 1 }}
                >
                  {current ? "Active plan" : `Upgrade to ${t.tier}`}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <AccountTools activeWsId={activeWsId} onDataDeleted={onDataDeleted} />

      <div className="panel-card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>Integrations</div>
        {(connections || []).length === 0 && <p className="font-body" style={{ fontSize: 13, color: "var(--tmuted)" }}>No integrations configured.</p>}
        {(connections || []).map((c) => (
          <div key={c.key} className="flex items-center justify-between" style={{ padding: "10px 0", borderBottom: "1px solid var(--cardborder)" }}>
            <span className="font-body" style={{ fontSize: 13.5, color: "var(--tprimary)" }}>{c.label || c.key}</span>
            <span className="font-mono" style={{ fontSize: 11, color: c.connected ? "var(--ok)" : "var(--tmuted)", display: "inline-flex", alignItems: "center", gap: 6 }}>
              {c.connected ? <CheckCircle2 size={13} /> : <Circle size={13} />}{c.connected ? "Connected" : "Offline"}
            </span>
          </div>
        ))}
      </div>

      <div className="panel-card" style={{ padding: 22 }}>
        <div className="eyebrow" style={{ marginBottom: 14 }}>Workspace</div>
        <div className="flex flex-wrap gap-2.5">
          <button onClick={onOpenContent} className="btn-secondary font-mono" data-testid="settings-content-library" style={{ fontSize: 12, padding: "9px 14px" }}>
            <Library size={14} /> Content Library
          </button>
          <button onClick={onReplayOnboarding} className="btn-secondary font-mono" data-testid="settings-replay-onboarding" style={{ fontSize: 12, padding: "9px 14px" }}>
            <RotateCcw size={14} /> Replay walkthrough
          </button>
          <button onClick={onLogout} className="btn-secondary font-mono" data-testid="settings-logout" style={{ fontSize: 12, padding: "9px 14px", color: "var(--err)" }}>
            Log out
          </button>
        </div>
        <a href={poweredByUrl} target="_blank" rel="noreferrer" className="app-link font-body" style={{ fontSize: 11.5, display: "inline-block", marginTop: 16 }}>{poweredBy}</a>
      </div>
    </div>
  );
}
